import { JSONContent } from "@tiptap/vue-3"
import { AppContext, h, render } from "vue"

import { getHrefByType } from "@/components/Reference/helpers/getHrefByType"

import { s3_base_url } from "@/app.config"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { fixColWidthForEditorTable, replaceHtmlTagsV2 } from "@/utils/helpers"

import { Context } from "../services/decorator.service"
import { Base } from "./Base"

interface Refer {
  id: string
  type: ReferenceType
  input: string
  attrs: any
}

interface Attachment {
  id: string
  type: AttachmentType
  input: string
  attrs: any
}

type AttachmentType = "ATTACHMENT"
type ReferInfo = Record<string, unknown>
type ReferUpdate = ReferInfo

export const parseEditorNodeData = (data?: string, replaceHtmlQuotes = false) => {
  if (!data) return {}

  try {
    const rawData = window.atob(data)
    const normalize = (value: string) => (replaceHtmlQuotes ? value.replace(/&quot;/g, '"') : value)

    try {
      return JSON.parse(normalize(decodeURI(rawData)))
    } catch {
      return JSON.parse(normalize(rawData))
    }
  } catch {
    return {}
  }
}

@Context
export class EditorData extends Base<EditorData> {
  $context: AppContext
  text: string
  json: JSONContent
  html: string
  refers: Refer[] = []
  attachments: Attachment[] = []
  mapComponent?: { [K in ReferenceType]?: any }
  constructor(mapComponent?: { [K in ReferenceType | AttachmentType]?: any }) {
    super()
    this.mapComponent = mapComponent
  }

  setText(str: string) {
    this.text = str
  }

  setJson(json: JSONContent) {
    this.json = json
  }

  setHtml(html: string) {
    this.html = html
    this.refers = []
    this.parseRefers(html)
    this.parseAttachments(html)
  }

  getHtmlElement() {
    const htmlText = this.html
    const textEl = document.createElement("span")
    textEl.innerHTML = htmlText
    if (this.mapComponent && (this.refers?.length || this.attachments?.length)) {
      ;[...this.refers, ...this.attachments].forEach(({ attrs, input, type }) => {
        if (this.mapComponent && this.mapComponent[type]) {
          const el = document.createElement("span")
          const nodeApp = h(this.mapComponent[type] || "span", {
            node: { attrs }
          })
          nodeApp.appContext = this.$context
          render(nodeApp, el)
          const id = input.split('id="')[1]?.split('"')[0]
          const refs = textEl.querySelectorAll("reference")
          refs.forEach(_el => {
            if (id === _el.id) {
              _el.replaceWith(el)
            }
          })
        }
      })
    }
    return textEl
  }

  getEmailText() {
    const origin = window.location.origin
    const htmlText = this.html
    const textEl = document.createElement("span")
    textEl.innerHTML = htmlText

    const ICON_CHECKED = `${s3_base_url}/static/checked-checkbox.png`
    const ICON_UNCHECKED = `${s3_base_url}/static/unchecked-checkbox.png`

    // Get all task lists
    const taskLists = Array.from(textEl.querySelectorAll('ul[data-type="taskList"]'))

    // Iterate in reverse order to process nested lists first (bottom-up approach).
    // This ensures we don't break the DOM structure of parent lists while handling children.
    taskLists.reverse().forEach(ul => {
      const container = document.createElement("div")

      // Select only direct children of the current list to avoid selecting nested items
      const listItems = ul.querySelectorAll(":scope > li")

      listItems.forEach(li => {
        const isChecked = li.getAttribute("data-checked") === "true"
        const currentIcon = isChecked ? ICON_CHECKED : ICON_UNCHECKED

        // Remove the standard editor label/input (not supported in emails)
        const label = li.querySelector("label")
        if (label) label.remove()

        // Get the remaining HTML content (text + already processed nested tables)
        const contentHtml = li.innerHTML

        // Construct a table layout with inline styles for email client compatibility
        const tableHtml = `
          <table border="0" cellpadding="0" cellspacing="0" style="border-collapse: collapse; width: 100%; border: 0; margin-bottom: 4px;">
            <tbody>
              <tr>
                <td style="border: none; width: 24px; min-width: 24px; padding: 0; vertical-align: top;">
                  <img src="${currentIcon}" width="24" height="24" alt="" style="display: block; border: 0;">
                </td>
                <td style="border: none; padding: 2px 0 0 8px; vertical-align: top;">
                  ${contentHtml}
                </td>
              </tr>
            </tbody>
          </table>
        `
        container.insertAdjacentHTML("beforeend", tableHtml)
      })

      // Replace the original <ul> with the new <div> container
      ul.replaceWith(container)
    })

    if (this.refers?.length || this.attachments?.length) {
      ;[...this.refers, ...this.attachments].forEach(({ attrs, input, type }) => {
        const id = input.split('id="')[1]?.split('"')[0]
        const refs = textEl.querySelectorAll("reference")
        const el = document.createElement("a")
        if (type === ReferenceType.USER || type === ReferenceType.PREVIOUS_TASK_OWNER) {
          el.href = attrs.linkHref
        } else if (type === ReferenceType.FILE_LINK) {
          el.href = attrs.linkHref || "#"
        } else {
          el.href = origin + attrs.linkHref
        }
        el.textContent = attrs.linkText
        refs.forEach(_el => {
          if (id === _el.id) {
            _el.replaceWith(el)
          }
        })
      })

      let html = textEl.innerHTML
      html = html.replace(/<\/a>\s+/g, "</a>")
      html = html.replace(/<\/a>(?!&nbsp;)/g, "</a>&nbsp;")
      textEl.innerHTML = html
    }
    return textEl.outerHTML.slice(6).slice(0, -7)
  }

  updateHtmlFromRefers(id: string, update: ReferUpdate, convertedFromId?: string) {
    if (!this.refers?.length) return

    const textEl = document.createElement("span")
    textEl.innerHTML = this.html
    const matchedId = convertedFromId || id

    this.refers.forEach((refer, i) => {
      const { attrs } = refer
      const currentInfo = this.parseReferenceInfo(attrs?.info)
      if (!currentInfo) return

      const currentInfoId = currentInfo?.id ?? attrs.id
      if (matchedId !== currentInfoId) return

      const nextInfo = convertedFromId ? { id } : { ...currentInfo }
      const nextAttrs = { ...attrs }
      const previousReferenceId = attrs.id

      if (convertedFromId) {
        nextInfo.id = id
        nextAttrs.id = id
      }

      this.applyReferenceUpdate(nextAttrs, nextInfo, currentInfo, update)
      this.updateConvertedReferenceHref(
        refer.type,
        nextAttrs,
        nextInfo,
        convertedFromId,
        Object.prototype.hasOwnProperty.call(update, "linkHref")
      )

      nextAttrs.info = JSON.stringify(nextInfo)
      refer.attrs = nextAttrs
      refer.id = nextAttrs.id
      refer.type = nextAttrs.configType

      const el = this.createReferenceElement(nextAttrs)
      this.refers[i].input = el.outerHTML

      const refs = textEl.querySelectorAll("reference")
      refs.forEach(_el => {
        if (previousReferenceId === _el.id) {
          _el.replaceWith(el.cloneNode(true))
        }
      })
    })

    this.html = textEl.innerHTML
  }

  getHtmlText() {
    return this.getHtmlElement().outerHTML.slice(6).slice(0, -7)
  }

  private parseRefers(html: string) {
    if (!html) return
    const refs = html.match(/<reference.+?reference>/g)
    if (refs?.length) {
      this.refers = refs.map(ref => {
        const data = ref.match(/data="([^"]+)"/)?.at(-1)
        const attrs = parseEditorNodeData(data, true)
        const info = attrs?.info || {}
        return {
          id: attrs.id,
          type: attrs.configType,
          attrs: { ...attrs, info },
          input: ref
        }
      })
    }
  }

  private parseAttachments(html: string) {
    if (!html) return
    const attachments = html.match(/<vue-component.+?vue-component>/g)
    if (attachments?.length) {
      this.attachments = attachments.map(att => {
        const data = att.split('data="')[1]?.split('"')?.[0]
        const attrs = parseEditorNodeData(data)
        const title = attrs?.title || ""
        return {
          id: attrs.id,
          type: "ATTACHMENT",
          attrs,
          input: att,
          title
        }
      })
    }
  }

  getAdditionalData(refer: Refer) {
    refer.id = ""
  }

  private parseReferenceInfo(info: unknown): ReferInfo | null {
    if (!info) return null

    if (typeof info === "string") {
      try {
        return JSON.parse(info)
      } catch {
        return null
      }
    }

    if (typeof info === "object") {
      return { ...(info as ReferInfo) }
    }

    return null
  }

  private applyReferenceUpdate(
    nextAttrs: Refer["attrs"],
    nextInfo: ReferInfo,
    currentInfo: ReferInfo,
    update: ReferUpdate
  ) {
    Object.entries(update).forEach(([key, value]) => {
      if (key === "title" && !update.linkText) {
        const title = currentInfo[key]
        if (title && nextAttrs.linkText) {
          nextAttrs.linkText = nextAttrs.linkText.replace(String(title), String(value))
        }
      }

      switch (key) {
        case "id":
          nextAttrs.id = value
          nextInfo.id = value
          return
        case "configType":
          nextAttrs.configType = value
          nextAttrs.type = this.getReferenceNodeType(value as ReferenceType)
          return
        case "type":
        case "linkText":
        case "linkHref":
        case "originUrl":
        case "loading":
        case "showDetails":
          nextAttrs[key] = value
          return
        default:
          nextInfo[key] = value
      }
    })
  }

  private updateConvertedReferenceHref(
    currentType: ReferenceType,
    nextAttrs: Refer["attrs"],
    nextInfo: ReferInfo,
    convertedFromId?: string,
    hasExplicitLinkHref = false
  ) {
    if (!convertedFromId || hasExplicitLinkHref || currentType === nextAttrs.configType) return

    const routeContext = this.getReferenceRouteContext(currentType, nextAttrs.linkHref)
    if (!routeContext || !nextAttrs.configType) return

    nextAttrs.linkHref = getHrefByType(
      nextAttrs.configType as ReferenceType,
      nextInfo,
      routeContext.accountId,
      routeContext.projectId
    )
  }

  private getReferenceRouteContext(currentType: ReferenceType, href?: string) {
    if (!href) return null

    const url = new URL(href, window.location.origin)
    const pathnameParts = url.pathname.split("/").filter(Boolean)
    const accountId = pathnameParts[0]
    const rawProjectId = pathnameParts[1]

    if (!accountId || !rawProjectId) return null

    const projectId =
      currentType === ReferenceType.ITEM || currentType === ReferenceType.TASK
        ? rawProjectId.match(/^(.+)-\d+$/)?.[1]
        : rawProjectId

    if (!projectId) return null

    return { accountId, projectId }
  }

  private getReferenceNodeType(configType: ReferenceType) {
    switch (configType) {
      case ReferenceType.USER:
      case ReferenceType.PREVIOUS_TASK_OWNER:
        return "referenceUser"
      case ReferenceType.PROJECT:
        return "referenceProject"
      case ReferenceType.MILESTONE:
        return "referenceMilestone"
      case ReferenceType.EPIC:
        return "referenceEpic"
      case ReferenceType.IDEA:
        return "referenceIdea"
      case ReferenceType.TEAM:
        return "referenceTeam"
      case ReferenceType.SPRINT:
        return "referenceSprint"
      case ReferenceType.ITEM:
        return "referenceItem"
      case ReferenceType.TASK:
        return "referenceTask"
      case ReferenceType.FILE_LINK:
        return "referenceFile"
      case ReferenceType.FILE:
      case ReferenceType.TIP:
      default:
        return ""
    }
  }

  private createReferenceElement(attrs: Refer["attrs"]) {
    const el = document.createElement("reference")
    const nodeType = attrs.type || this.getReferenceNodeType(attrs.configType)

    el.setAttribute("id", attrs.id ?? "")
    el.setAttribute("loading", attrs.loading)
    el.setAttribute("showDetails", attrs.showDetails)
    el.setAttribute("type", nodeType)
    el.setAttribute("configtype", attrs.configType ?? "")
    el.setAttribute("info", attrs.info ?? "")
    el.setAttribute("linkText", attrs.linkText ?? "")
    el.setAttribute("linkHref", attrs.linkHref ?? "")
    el.setAttribute("originUrl", attrs.originUrl ?? "")
    el.setAttribute("data", window.btoa(encodeURI(JSON.stringify({ ...attrs, type: nodeType }))))

    return el
  }
}

export class StructuredDescription {
  text = ""
  html = ""
  json = ""

  constructor(props?: Partial<StructuredDescription>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  get getTextFromHtml() {
    return replaceHtmlTagsV2(this.html)
  }

  getJsonObj() {
    return {
      text: this.text,
      html: this.html,
      json: this.json ? JSON.stringify(fixColWidthForEditorTable(JSON.parse(this.json))) : ""
    }
  }

  static updateHtmlByJson(structuredDescription: StructuredDescription) {
    // Ensure the JSON content is parsed into an object
    const jsonParsed =
      structuredDescription.json && typeof structuredDescription.json === "string"
        ? JSON.parse(structuredDescription.json)
        : structuredDescription.json

    // Extract all nodes of type 'taskList' from the JSON structure
    const taskLists = jsonParsed ? jsonParsed.content.filter((e: any) => e.type === "taskList") : []

    // Only proceed if there are task lists to process to avoid unnecessary DOM operations
    if (taskLists.length > 0) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(structuredDescription.html, "text/html")

      // Select all task items (li) from the HTML.
      // Note: This returns a flat list of all items across all task lists.
      const taskListHtml = doc.querySelectorAll('li[data-type="taskItem"]')

      // Use a global index to match the nested JSON structure with the flat HTML NodeList
      let globalIndex = 0

      taskLists.forEach((taskList: any) => {
        taskList.content.forEach((taskItem: any) => {
          // Determine the checked state from the JSON attributes
          const isChecked = taskItem.attrs?.checked || false

          // Find the corresponding HTML element using the running index
          const selectedHtmlItem = taskListHtml[globalIndex]

          if (selectedHtmlItem) {
            // Sync the HTML attribute with the actual JSON state
            selectedHtmlItem.setAttribute("data-checked", isChecked.toString())
            globalIndex++
          }
        })
      })

      // Serialize the modified DOM back to an HTML string and update editorData
      return doc.body.innerHTML
    }
    return structuredDescription.html
  }
}

export class StructuredDescriptionHelper extends StructuredDescription {
  constructor(props?: Partial<StructuredDescription>) {
    super(props)
  }

  get getTextFromHtml() {
    return replaceHtmlTagsV2(this.html)
  }
}
